import React, { ReactNode, useState } from "react";
import { LinhaTabela } from "shared/components/table/LinhaTabela";
import { RegistroTabela } from "shared/components/table/RegistroTabela";

import { RegistroAgendamentoContatoDTO } from "types/dto/RegistroAgendamentoContatoDTO";
import {
  TipoStatusAgendamentoContato,
  getStatusAgendamentoFormatado,
} from "constants/EnumTipoStatusAgendamentoContato";
import { Tooltip } from "antd";
import { BsFillEyeFill, BsFillPencilFill, BsFillTrashFill } from "react-icons/bs";
import { FaCheck } from "react-icons/fa";
import CustomTag from "shared/components/customTag/CustomTag";
import { usePostConcluirAgendamentoContatos } from "hooks/agendamentoContato/usePostConcluirAgendamentoContatos";
import { RequestConcluirAgendamento } from "types/request/RequestConcluirAgendamento";
import ModalEdicaoVisualizacaoAgendarContato from "./ModalEdicaoVisualizacaoAgendarContato";
import { useDeleteAgendamentoContato } from "hooks/agendamentoContato/useDeleteAgendamentoContato";
import showAlert from "shared/util/functions/alert/showAlert";
import { useAgendamentoContatoContext } from "context/gestaoCobranca/AgendamentoContatoContext";

interface RenderRowsProps {
  registros: RegistroAgendamentoContatoDTO[];
}

export const RenderRowsAgendamentos: React.FC<RenderRowsProps> = (props) => {
  const { setIsEdicao } = useAgendamentoContatoContext();
  const [agendamentoContatoId, setAgendamentoContatoId] = useState<number>();
  const [agendamentoContatoUuid, setAgendamentoContatoUuid] = useState<string>();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isVisualizacao, setIsVisualizacao] = useState<boolean>(false);

  const { mutate: postConcluirAgendamento } = usePostConcluirAgendamentoContatos();
  const { mutate: deleteExcluirAgendamento } = useDeleteAgendamentoContato();
  const visualizarEditarAgendamento = (
    agendamentoId: number,
    agendamentoUuid: string,
    isVisualizacao: boolean
  ) => {
    setAgendamentoContatoId(agendamentoId);
    setAgendamentoContatoUuid(agendamentoUuid);
    setIsVisualizacao(isVisualizacao);
    setShowModal(true);
  };
  const {
    possuiPermissaoVisualizarAgendamento,
    possuiPermissaoEditarAgendamento,
    possuiPermissaoExcluirAgendamento,
    possuiPermissaoExecutarAgendamento,
  } = useAgendamentoContatoContext();

  const getBackgroundColorByStatus = (status: TipoStatusAgendamentoContato): string => {
    switch (status) {
      case TipoStatusAgendamentoContato.AGENDADO_HOJE:
        return "#f5b93d";
      case TipoStatusAgendamentoContato.ATRASADO:
        return "#e8654a";
      case TipoStatusAgendamentoContato.CONCLUIDO:
        return "#73bd8a";
      case TipoStatusAgendamentoContato.AGENDADO:
        return "#6987c5";
      case TipoStatusAgendamentoContato.PROXIMO_7_DIAS:
        return "#ed8f37";
      default:
        return "";
    }
  };

  const getTooltipTitleByStatus = (status: TipoStatusAgendamentoContato): string => {
    return status === TipoStatusAgendamentoContato.CONCLUIDO ? "Concluído" : "Concluir agendamento";
  };

  const getIconColorByStatus = (status: TipoStatusAgendamentoContato): string => {
    return status === TipoStatusAgendamentoContato.CONCLUIDO ? "#73bd8a" : "#a69ae7";
  };

  const onDelete = (agendamentoId: number) => {
    showAlert({
      texto: "Você realmente deseja excluir?",
      tipoAlerta: "CONFIRMAR_CANCELAR_ALERT",
      onConfirm: () => {
        deleteExcluirAgendamento(agendamentoId);
      },
    });
  };

  const renderRegistros = (agendamentos: RegistroAgendamentoContatoDTO[]): ReactNode => {
    return agendamentos.map((agendamento, index) => {
      return (
        <LinhaTabela
          id={`id-registro-agendamento-${index}`}
          key={`key-registro-agendamento-${index}`}
        >
          <RegistroTabela
            id={`condominio-agendamento-${agendamento.id}`}
            key={`key-condominio-agendamento-${agendamento.id}`}
          >
            <span>{agendamento.nomeCondominio}</span>
          </RegistroTabela>

          <RegistroTabela
            id={`data-hora-agendamento-${agendamento.dataHoraAgendamento}`}
            key={`key-data-hora-agendamento-${agendamento.dataHoraAgendamento}`}
          >
            <span>{agendamento.dataHoraAgendamento}</span>
          </RegistroTabela>

          <RegistroTabela
            id={`unidade-sacado-agendamento-${agendamento.unidadeSacado}`}
            key={`key-unidade-sacado-agendamento-${agendamento.unidadeSacado}`}
          >
            <span>{agendamento.unidadeSacado}</span>
          </RegistroTabela>

          <RegistroTabela
            id={`responsavel-agendamento-${agendamento.nomeResponsavel}`}
            key={`key-responsavel-agendamento-${agendamento.nomeResponsavel}`}
          >
            <span>{agendamento.nomeResponsavel}</span>
          </RegistroTabela>

          <RegistroTabela
            id={`status-agendamento-${agendamento.tipoStatusAgendamento}`}
            key={`key-status-agendamento-${agendamento.tipoStatusAgendamento}`}
          >
            <CustomTag
              bacgroundColor={getBackgroundColorByStatus(agendamento.tipoStatusAgendamento)}
            >
              {getStatusAgendamentoFormatado(agendamento.tipoStatusAgendamento)}
            </CustomTag>
          </RegistroTabela>

          <RegistroTabela id={`acoes-agendamento-${index}`} key={`key-acoes-agendamento-${index}`}>
            {possuiPermissaoExecutarAgendamento && (
              <Tooltip
                title={getTooltipTitleByStatus(agendamento.tipoStatusAgendamento)}
                placement="top"
              >
                <a
                  onClick={() => {
                    postConcluirAgendamento({
                      agendamentoId: agendamento.id,
                    } as RequestConcluirAgendamento);
                  }}
                >
                  <FaCheck
                    className="cursor-pointer me-2"
                    size={15}
                    color={getIconColorByStatus(agendamento.tipoStatusAgendamento)}
                  />
                </a>
              </Tooltip>
            )}
            {possuiPermissaoEditarAgendamento && (
              <Tooltip title="Editar agendamento" placement="top">
                <a
                  onClick={() => {
                    setIsEdicao(true);
                    visualizarEditarAgendamento(agendamento.id, agendamento.uuid, false);
                  }}
                >
                  <BsFillPencilFill className="cursor-pointer me-2" size={15} color="#a69ae7" />
                </a>
              </Tooltip>
            )}
            {possuiPermissaoVisualizarAgendamento && (
              <Tooltip title="Visualizar agendamento" placement="top">
                <a
                  onClick={() => {
                    visualizarEditarAgendamento(agendamento.id, agendamento.uuid, true);
                  }}
                >
                  <BsFillEyeFill className="cursor-pointer me-2" size={15} color="#a69ae7" />
                </a>
              </Tooltip>
            )}
            {possuiPermissaoExcluirAgendamento && (
              <Tooltip title="Excluir agendamento" placement="top">
                <a
                  onClick={() => {
                    onDelete(agendamento.id);
                  }}
                >
                  <BsFillTrashFill className="cursor-pointer me-2" size={15} color="#a69ae7" />
                </a>
              </Tooltip>
            )}
          </RegistroTabela>
        </LinhaTabela>
      );
    });
  };

  return (
    <>
      {props.registros.length > 0 && renderRegistros(props.registros)}
      {agendamentoContatoId && (
        <ModalEdicaoVisualizacaoAgendarContato
          agendamentoId={agendamentoContatoId}
          agendamentoUuid={agendamentoContatoUuid ?? ""}
          showModal={showModal}
          onCloseModal={() => {
            setIsEdicao(false);
            setShowModal(false);
          }}
          isVisualizacao={isVisualizacao}
        />
      )}
    </>
  );
};
